import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query-keys";
import { resendConnectionService } from "@/services/resend-connection";

import type {
  CreateResendConnectionReq,
  UpdateResendConnectionReq,
} from "@artisancode/api-types";

export function useResendConnections() {
  return useQuery({
    queryKey: queryKeys.resendConnections.all,
    queryFn: resendConnectionService.list,
  });
}

function useInvalidateResendConnections() {
  const qc = useQueryClient();
  return () =>
    qc.invalidateQueries({ queryKey: queryKeys.resendConnections.all });
}

export function useCreateResendConnection() {
  const invalidate = useInvalidateResendConnections();
  return useMutation({
    mutationFn: (data: CreateResendConnectionReq) =>
      resendConnectionService.create(data),
    onSuccess: invalidate,
  });
}

export function useUpdateResendConnection() {
  const invalidate = useInvalidateResendConnections();
  return useMutation({
    mutationFn: ({ id, ...data }: UpdateResendConnectionReq & { id: string }) =>
      resendConnectionService.update(id, data),
    onSuccess: invalidate,
  });
}

export function useRemoveResendConnection() {
  const invalidate = useInvalidateResendConnections();
  return useMutation({
    mutationFn: (id: string) => resendConnectionService.remove(id),
    onSuccess: invalidate,
  });
}

export function useSetPrimaryResendConnection() {
  const invalidate = useInvalidateResendConnections();
  return useMutation({
    mutationFn: (id: string) => resendConnectionService.setPrimary(id),
    onSuccess: invalidate,
  });
}
